import { useMemo } from "react";

import { styled } from "@mui/material";
import { Typography } from "components";
import {
  blueLight,
  error,
  gray,
  purple,
  success,
  warning,
} from "configs/colors";
import { ApprovalRequestStatus, Status } from "services";
import { i18n } from "translations";

interface Props {
  type?: "success" | "error" | "warning" | "info" | "default" | "secondary";
  label?: string;
  status?: number;
  unpaid?: boolean;
  approved?: boolean;
  requestStatus?: string;
}

const StyledChip = styled("div")<{
  bgColor: string;
  borderColor: string;
  color: string;
}>(({ bgColor, borderColor, color }) => ({
  border: `1px solid black`,
  borderColor,
  backgroundColor: bgColor,
  borderRadius: "16px",
  color,
  padding: "1px 10px",
  display: "flex",
  alignItems: "center",
}));

const ChipStatus: React.FC<Props> = ({
  status,
  type,
  label,
  unpaid,
  approved,
  requestStatus,
}) => {
  const statusMapping = useMemo(() => {
    let style: React.ComponentProps<typeof StyledChip> =
      {} as React.ComponentProps<typeof StyledChip>;
    if (type === "success") {
      style = {
        ...style,
        bgColor: success[50],
        borderColor: success[200],
        color: success[700],
      };
    }
    if (type === "error") {
      style = {
        ...style,
        bgColor: error[50],
        borderColor: error[200],
        color: error[700],
      };
    }
    if (type === "warning") {
      style = {
        ...style,
        bgColor: warning[50],
        borderColor: warning[200],
        color: warning[700],
      };
    }
    if (type === "info") {
      style = {
        ...style,
        bgColor: blueLight[50],
        borderColor: blueLight[200],
        color: blueLight[700],
      };
    }
    if (type === "secondary") {
      style = {
        ...style,
        bgColor: purple[50],
        borderColor: purple[200],
        color: purple[700],
      };
    }
    if (type === "default") {
      style = {
        ...style,
        bgColor: gray[50],
        borderColor: gray[200],
        color: gray[700],
      };
    }
    if (status === Status.Active) {
      return {
        bgColor: success[50],
        borderColor: success[200],
        color: success[700],
      };
    }
    if (status === Status.Inactive) {
      return {
        bgColor: gray[50],
        borderColor: gray[200],
        color: gray[700],
      };
    }

    if (typeof unpaid !== "undefined") {
      if (unpaid) {
        return {
          bgColor: warning[50],
          borderColor: warning[200],
          color: warning[700],
        };
      }
      return {
        bgColor: success[50],
        borderColor: success[200],
        color: success[700],
      };
    }

    if (typeof approved !== "undefined") {
      if (approved) {
        return {
          bgColor: success[50],
          borderColor: success[200],
          color: success[700],
        };
      }
      return {
        bgColor: warning[50],
        borderColor: warning[200],
        color: warning[700],
      };
    }

    if (typeof requestStatus !== "undefined") {
      if (requestStatus === ApprovalRequestStatus.APPROVED) {
        return {
          bgColor: success[50],
          borderColor: success[200],
          color: success[700],
        };
      }
      if (
        requestStatus === ApprovalRequestStatus.REJECTED ||
        requestStatus === ApprovalRequestStatus.DELETED ||
        requestStatus === ApprovalRequestStatus.CANCEL
      ) {
        return {
          bgColor: error[50],
          borderColor: error[200],
          color: error[700],
        };
      }
      if (
        requestStatus === ApprovalRequestStatus.DRAFT ||
        requestStatus === ApprovalRequestStatus.SKIP
      ) {
        return {
          bgColor: gray[50],
          borderColor: gray[200],
          color: gray[700],
        };
      }
      if (requestStatus === ApprovalRequestStatus.PENDING) {
        return {
          bgColor: warning[50],
          borderColor: warning[200],
          color: warning[700],
        };
      }
    }

    return style;
  }, [approved, requestStatus, status, type, unpaid]);

  const labelBtn = useMemo(() => {
    if (label) return label;
    if (status === Status.Active) return i18n.t("active");
    if (status === Status.Inactive) return i18n.t("inactive");
    if (typeof unpaid !== "undefined") {
      return unpaid ? i18n.t("unpaidLeave") : i18n.t("paidLeave");
    }
    if (typeof approved !== "undefined") {
      return approved ? i18n.t("approved") : i18n.t("waitingForApproval");
    }
    if (typeof requestStatus !== "undefined") {
      if (requestStatus === ApprovalRequestStatus.APPROVED) {
        return i18n.t("approved");
      }
      if (requestStatus === ApprovalRequestStatus.REJECTED) {
        return i18n.t("rejected");
      }
      if (requestStatus === ApprovalRequestStatus.CANCEL) {
        return i18n.t("canceled");
      }
      if (requestStatus === ApprovalRequestStatus.PENDING) {
        return i18n.t("waitingForApproval");
      }
      if (requestStatus === ApprovalRequestStatus.DELETED) {
        return i18n.t("deleted");
      }
      if (requestStatus === ApprovalRequestStatus.DRAFT) {
        return i18n.t("draft");
      }
      if (requestStatus === ApprovalRequestStatus.SKIP) {
        return i18n.t("skipped");
      }
    }
    return "";
  }, [approved, label, requestStatus, status, unpaid]);
  return (
    <StyledChip
      bgColor={statusMapping.bgColor}
      color={statusMapping.color}
      borderColor={statusMapping.borderColor}
    >
      <Typography type="TextXS" fontWeight="500">
        {labelBtn}
      </Typography>
    </StyledChip>
  );
};

export default ChipStatus;
